/**
 * ADD ASSET GROUPS — /google-ads/<sales|local>/create/assetgroup[?campaign_id=…]
 *
 * One existing Performance Max campaign, many asset-group drafts with the
 * live multi-surface preview. New groups start from the campaign's existing
 * brand (final URL, business name, logo assets — reused, never re-uploaded).
 * Resumable: created groups are remembered by draft id.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { googleAdsService, GoogleAdsCampaignSummary, GoogleAdsUserList, PmaxCampaignMeta } from '@/services/google-ads.service';
import { BackTo, biddingLabel, errMsg, formatMicros, Pill, Section, statusOf, Tag, useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { ImageDraft, Pub, uid } from '@/components/google-ads/objectives/objectiveShared';
import { ConnectFirst, GoalStrip, PageSpinner, SaveBar } from '@/components/google-ads/objectives/wizardChrome';
import {
  AssetGroupCard, AssetGroupDraft, assetGroupIssues, AssetGroupPreview, brandOfGroup, cloneAssetGroup, isPmaxCampaign, newAssetGroup, PmaxBrand,
  PmaxObjectiveConfig, publishAssetGroup, writeAssetInto,
} from './pmaxShared';

type Patch<T> = Partial<T> | ((cur: T) => Partial<T>);

export default function PmaxAssetGroupPage({ objective: cfg }: { objective: PmaxObjectiveConfig }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { auth, account, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? '';

  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [meta, setMeta] = useState<Record<string, PmaxCampaignMeta>>({});
  const [campaignId, setCampaignId] = useState(params.get('campaign_id') || '');
  const [existing, setExisting] = useState<number | null>(null);
  const [brand, setBrand] = useState<Partial<PmaxBrand> | undefined>(undefined);
  const [audiences, setAudiences] = useState<GoogleAdsUserList[] | null>(null);
  const campaign = campaigns?.find((c) => c.id === campaignId);

  const first = useMemo(() => newAssetGroup(1), []);
  const [groups, setGroups] = useState<AssetGroupDraft[]>([first]);
  const [openId, setOpenId] = useState(first.id);
  const [activeId, setActiveId] = useState(first.id);
  const patch = (gid: string, p: Patch<AssetGroupDraft>) =>
    setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...(typeof p === 'function' ? p(g) : p) } : g)));

  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const madeRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!accountUid) return;
    Promise.all([
      googleAdsService.listCampaigns(accountUid),
      googleAdsService.performanceMax.campaignMeta(accountUid).catch(() => ({} as Record<string, PmaxCampaignMeta>)),
    ]).then(([list, m]) => { setCampaigns(list.filter((c) => isPmaxCampaign(c) && c.status !== 'REMOVED')); setMeta(m); })
      .catch(() => setCampaigns([]));
    googleAdsService.listAudiences(accountUid).then(setAudiences).catch(() => setAudiences([]));
  }, [accountUid]);

  // The campaign's first asset group carries its brand — final URL, business name and logo assets.
  useEffect(() => {
    setExisting(null); setBrand(undefined);
    if (!accountUid || !campaignId || !account?.customerId) return;
    let alive = true;
    (async () => {
      try {
        const gs = await googleAdsService.performanceMax.listAssetGroups(accountUid, campaignId);
        if (!alive) return;
        setExisting(gs.length);
        const g = gs[0];
        if (!g) return;
        const assets = await googleAdsService.performanceMax.listAssetGroupAssets(accountUid, g.id);
        if (!alive) return;
        const logoDraft = (a: typeof assets[number]): ImageDraft => ({
          id: uid(), url: a.imageUrl ?? '', note: a.name ?? 'logo', width: a.width, height: a.height, resourceName: `customers/${account.customerId}/assets/${a.assetId}`,
        });
        const b: Partial<PmaxBrand> = {
          finalUrl: g.finalUrls[0] ?? '',
          businessName: assets.find((a) => a.fieldType === 'BUSINESS_NAME')?.text ?? '',
          logos: assets.filter((a) => a.fieldType === 'LOGO' && a.imageUrl).map(logoDraft),
          wideLogos: assets.filter((a) => a.fieldType === 'LANDSCAPE_LOGO' && a.imageUrl).map(logoDraft),
        };
        setBrand(b);
        setGroups((cur) => cur.map((x) => ({
          ...x,
          finalUrl: x.finalUrl || b.finalUrl || '', businessName: x.businessName || b.businessName || '',
          logos: x.logos.length ? x.logos : (b.logos ?? []).map((i) => ({ ...i })),
          wideLogos: x.wideLogos.length ? x.wideLogos : (b.wideLogos ?? []).map((i) => ({ ...i })),
        })));
      } catch { if (alive) setExisting(null); }
    })();
    return () => { alive = false; };
  }, [accountUid, campaignId, account?.customerId]);

  const mine = (campaigns ?? []).filter((c) => (meta[c.id]?.objective ?? 'sales') === cfg.key);
  const others = (campaigns ?? []).filter((c) => (meta[c.id]?.objective ?? 'sales') !== cfg.key);

  const issues = useMemo(() => {
    const i: string[] = [];
    if (!campaignId) i.push('Pick the campaign these asset groups belong to.');
    groups.forEach((g, n) => { const gi = assetGroupIssues(g); if (gi.length) i.push(`${g.name.trim() || `Asset group ${n + 1}`}: needs ${gi.join(', ')}.`); });
    return i;
  }, [campaignId, groups]);

  const submit = async () => {
    if (!accountUid || issues.length) return;
    setSaving(true); setError(null);
    const mark = (k: string, v: Pub) => setPub((m) => ({ ...m, [k]: v }));
    try {
      for (const g of groups) {
        if (madeRef.current[g.id]) continue;
        mark(g.id, 'running');
        try {
          const r = await publishAssetGroup(accountUid, campaignId, g, (field, id, rn) => patch(g.id, writeAssetInto(field, id, rn)));
          madeRef.current[g.id] = r.assetGroupId;
          if (r.signals?.error) toast.error(`${g.name.trim()}: created, but its audience signals were refused — ${r.signals.error}`);
        } catch (e) { mark(g.id, 'failed'); setError(`${g.name.trim()}: ${errMsg(e)}`); return; }
        mark(g.id, 'done');
      }
      toast.success(groups.length === 1 ? 'Asset group created — paused until you enable it.' : `${groups.length} asset groups created — paused until you enable them.`);
      navigate(cfg.base);
    } finally { setSaving(false); }
  };

  const active = groups.find((g) => g.id === activeId) ?? groups[0] ?? null;
  const st = campaign ? statusOf(campaign.status, campaign.primaryStatus) : null;
  const m = campaign ? meta[campaign.id] : undefined;

  return (
    <>
      <Topbar title="New asset groups" breadcrumb={`Google Ads / ${cfg.title}`} description="Text, images and videos Google mixes into every format"
        back={<BackTo to={cfg.base} label={`Back to ${cfg.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && <PageSpinner />}
        {auth !== undefined && !ready && <ConnectFirst what="asset groups" />}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <div className="min-w-0 flex-1 space-y-4">
              <GoalStrip items={[
                { label: 'Goal', value: cfg.goal },
                { label: 'Type', value: 'Performance Max asset groups' },
                { label: 'Adding to', value: campaign?.name ?? 'pick a campaign' },
              ]}>
                <Tag>starts paused</Tag>
              </GoalStrip>

              <Section title="Campaign" hint="The Performance Max campaign these asset groups belong to.">
                <Select className="h-10 rounded-lg" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} disabled={Object.keys(madeRef.current).length > 0}>
                  <option value="">{campaigns === null ? 'Loading campaigns…' : campaigns.length ? 'Pick a Performance Max campaign' : 'No Performance Max campaigns yet'}</option>
                  {mine.length > 0 && <optgroup label={`${cfg.title} campaigns`}>{mine.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
                  {others.length > 0 && <optgroup label="Other Performance Max campaigns">{others.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
                </Select>
                {campaign && st && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-2xs text-text-muted">
                    <Pill {...st} />
                    {biddingLabel(campaign.biddingStrategyType) && <Tag>{biddingLabel(campaign.biddingStrategyType)}</Tag>}
                    {m?.targetCpaMicros && <Tag>target CPA {formatMicros(m.targetCpaMicros, currency)}</Tag>}
                    {m?.targetRoas && <Tag>target ROAS {Math.round(m.targetRoas * 100)}%</Tag>}
                    {formatMicros(campaign.budgetAmountMicros, currency) && <Tag>{formatMicros(campaign.budgetAmountMicros, currency)} / day</Tag>}
                    {existing !== null && <span>{existing} asset group{existing === 1 ? '' : 's'} already</span>}
                    {brand?.finalUrl && <span>· new groups start from {brand.finalUrl.replace(/^https?:\/\//, '')}</span>}
                  </div>
                )}
              </Section>

              {groups.map((g, n) => (
                <AssetGroupCard key={g.id} group={g} index={n} open={openId === g.id} onToggle={() => setOpenId(openId === g.id ? '' : g.id)}
                  state={pub[g.id]} made={!!madeRef.current[g.id]} busy={saving} onChange={(p) => patch(g.id, p)}
                  focused={activeId === g.id} onFocus={() => setActiveId(g.id)} audiences={audiences}
                  onDuplicate={() => { const c = cloneAssetGroup(g); setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), c, ...gs.slice(i + 1)]; }); setOpenId(c.id); setActiveId(c.id); }}
                  onRemove={groups.length > 1 ? () => setGroups((gs) => gs.filter((x) => x.id !== g.id)) : undefined} />
              ))}

              <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={saving}
                onClick={() => { const g = newAssetGroup(groups.length + 1, brandOfGroup(groups[groups.length - 1]) ?? brand); setGroups((gs) => [...gs, g]); setOpenId(g.id); setActiveId(g.id); }}>
                Add another asset group
              </Button>

              <SaveBar issues={issues} error={error} saving={saving} onSave={submit} onCancel={() => navigate(cfg.base)}
                label={groups.length === 1 ? 'Create asset group' : `Create ${groups.length} asset groups`} />
            </div>

            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24"><AssetGroupPreview group={active} /></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
